/* ==========================================================================
   AVICOO — HERO BEHAVIOUR
   Visual Task 02 · Video control, scroll cue, reduced-motion handling
   --------------------------------------------------------------------------
   Registers into window.AVICOO. Creates no new global.

   THEME EDITOR LIFECYCLE
   avicoo-core.js dispatches init on shopify:section:load and destroy on
   shopify:section:unload, passing the section element as scope. Both paths
   are handled below, so adding, moving, duplicating or deleting a hero in
   the editor rebinds correctly without a page refresh.

   DUPLICATE LISTENER PREVENTION
   Every bound hero is stamped with a state object on the element itself.
   initHero returns immediately if that stamp is present, so repeated init
   calls — which happen on every section load for every registered module —
   can never attach a second set of listeners. destroyHero runs the stored
   teardown functions and clears the stamp.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.AVICOO) return;

  var STATE_KEY = '_avcHeroState';

  /* ------------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */

  function prefersReducedMotion() {
    if (typeof window.AVICOO.prefersReducedMotion === 'function') {
      return window.AVICOO.prefersReducedMotion();
    }
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function strings() {
    var s = window.accessibilityStrings || {};
    return {
      pause: s.pauseSlideshow || 'Pause video',
      play: s.playSlideshow || 'Play video',
    };
  }

  function on(state, target, type, handler, options) {
    target.addEventListener(type, handler, options);
    state.cleanup.push(function () {
      target.removeEventListener(type, handler, options);
    });
  }

  /* ------------------------------------------------------------------------
     Video
     ---------------------------------------------------------------------- */

  function bindVideo(state) {
    var hero = state.hero;
    var video = hero.querySelector('[data-avc-hero-video]');
    var toggle = hero.querySelector('[data-avc-hero-video-toggle]');

    if (!video || !toggle) return;

    var labels = strings();

    function sync() {
      var paused = video.paused;
      toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      toggle.setAttribute('aria-label', paused ? labels.play : labels.pause);
    }

    function onToggleClick() {
      if (video.paused) {
        var attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch(function () {
            sync();
          });
        }
      } else {
        video.pause();
      }
    }

    on(state, toggle, 'click', onToggleClick);
    on(state, video, 'play', sync);
    on(state, video, 'pause', sync);

    /* Motion that plays automatically must not run for users who have asked
       for reduced motion. The control remains available so they can opt in. */
    if (prefersReducedMotion()) {
      video.autoplay = false;
      video.pause();
    }

    sync();

    state.cleanup.push(function () {
      try {
        video.pause();
      } catch (error) {
        /* Element may already be detached during an editor unload. */
      }
    });
  }

  /* ------------------------------------------------------------------------
     Scroll cue
     ---------------------------------------------------------------------- */

  function bindScrollCue(state) {
    var hero = state.hero;
    var cue = hero.querySelector('[data-avc-hero-scroll-cue]');

    if (!cue) return;

    function onCueClick(event) {
      var href = cue.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return;

      var target = document.getElementById(href.slice(1));
      if (!target) return;

      event.preventDefault();

      target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });

      /* Move focus so keyboard and screen-reader users land where the
         pointer user lands. The target carries tabindex="-1". */
      target.focus({ preventScroll: true });
    }

    on(state, cue, 'click', onCueClick);
  }

  /* ------------------------------------------------------------------------
     Lifecycle
     ---------------------------------------------------------------------- */

  function initHero(hero) {
    if (hero[STATE_KEY]) return;

    var state = {
      hero: hero,
      cleanup: [],
    };

    hero[STATE_KEY] = state;

    try {
      bindVideo(state);
      bindScrollCue(state);
    } catch (error) {
      if (window.Shopify && window.Shopify.designMode) {
        console.error('[AVICOO] hero init failed', error);
      }
    }
  }

  function destroyHero(hero) {
    var state = hero[STATE_KEY];
    if (!state) return;

    for (var i = 0; i < state.cleanup.length; i++) {
      try {
        state.cleanup[i]();
      } catch (error) {
        /* Teardown must never throw during an editor unload. */
      }
    }

    state.cleanup.length = 0;
    hero[STATE_KEY] = null;
  }

  function eachHero(scope, callback) {
    var root = scope && scope.querySelectorAll ? scope : document;

    if (root.matches && root.matches('[data-avc-hero]')) {
      callback(root);
    }

    var found = root.querySelectorAll('[data-avc-hero]');
    for (var i = 0; i < found.length; i++) {
      callback(found[i]);
    }
  }

  window.AVICOO.register(
    'hero',
    function (scope) {
      eachHero(scope, initHero);
    },
    function (scope) {
      eachHero(scope, destroyHero);
    }
  );
})();